// 伺服器 API：帳號、雲端心智圖。錯誤以 code 回傳，前端依語言翻譯。
import { parseOutline, serializeOutline, type MindMap } from '@sind/core';
import type { MapStorage } from './storage.js';
import { t, lang, type I18nKey } from './i18n.js';

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, readonly data: Record<string, unknown>) {
    super(code);
  }
}

// 呼叫 JSON API；POST 自動帶上介面語言（伺服器據此決定信件語言）。
export async function api<T = Record<string, unknown>>(method: string, url: string, body?: Record<string, unknown>): Promise<T> {
  const payload = body ? JSON.stringify({ language: lang, ...body }) : undefined;
  const response = await fetch(url, {
    method,
    credentials: 'same-origin',
    headers: body ? { 'Content-Type': 'application/json' } : {},
    // keepalive：關閉分頁時最後一次存檔也能送出（瀏覽器限制 64 KB 以內）。
    ...(payload ? { body: payload, keepalive: payload.length < 60_000 } : {}),
  });
  const data = await response.json().catch(() => ({})) as Record<string, unknown>;
  if (!response.ok) throw new ApiError(response.status, String(data.code ?? 'unknown'), data);
  return data as T;
}

// 錯誤 → 使用者看得懂的訊息。
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const key = `err.${error.code}` as I18nKey;
    const text = t(key);
    if (error.code === 'wrong_code' && typeof error.data.attemptsLeft === 'number') return `${text} (${error.data.attemptsLeft})`;
    return text === key ? t('err.unknown') : text;
  }
  return error instanceof Error ? `${t('error.network')}: ${error.message}` : t('err.unknown');
}

export interface User { id: string; email: string; name: string; language: string }
export interface MapSummary { id: string; title: string; shared: boolean; createdAt: number; updatedAt: number }
interface MapDetail extends MapSummary { outline: string }

export async function me(): Promise<User | null> {
  try {
    return (await api<{ user: User | null }>('GET', '/api/auth/me')).user;
  } catch {
    return null;
  }
}

export const listMaps = async () => (await api<{ maps: MapSummary[] }>('GET', '/api/maps')).maps;

export async function createMap(map: MindMap): Promise<MapDetail> {
  return api<MapDetail>('POST', '/api/maps', { outline: serializeOutline(map, { ids: 'all' }) });
}

export async function loadMap(id: string): Promise<{ map: MindMap; detail: MapDetail }> {
  const detail = await api<MapDetail>('GET', `/api/maps/${encodeURIComponent(id)}`);
  const { map } = parseOutline(detail.outline);
  if (!map) throw new Error('corrupted map');
  return { map, detail };
}

export const deleteMap = (id: string) => api('DELETE', `/api/maps/${encodeURIComponent(id)}`);

export async function shareCloudMap(id: string): Promise<string> {
  return (await api<{ url: string }>('POST', `/api/maps/${encodeURIComponent(id)}/share`, {})).url;
}

/** 雲端儲存：以 updatedAt 做樂觀鎖；衝突時交給 onConflict 決定覆蓋或載入新版。 */
export class CloudStorage implements MapStorage {
  readonly savedLabel = t('status.cloud');

  constructor(
    readonly id: string,
    private updatedAt: number,
    private readonly onConflict: (storage: CloudStorage) => Promise<'overwrite' | 'reload'>,
  ) {}

  async save(map: MindMap): Promise<void> {
    const outline = serializeOutline(map, { ids: 'all' });
    try {
      const result = await api<{ updatedAt: number }>('PUT', `/api/maps/${encodeURIComponent(this.id)}`, { outline, baseUpdatedAt: this.updatedAt });
      this.updatedAt = result.updatedAt;
    } catch (error) {
      if (!(error instanceof ApiError) || error.code !== 'conflict') throw error;
      if (await this.onConflict(this) === 'overwrite') {
        const result = await api<{ updatedAt: number }>('PUT', `/api/maps/${encodeURIComponent(this.id)}`, { outline, force: true });
        this.updatedAt = result.updatedAt;
      }
    }
  }

  markLoaded(updatedAt: number): void {
    this.updatedAt = updatedAt;
  }
}
